import { useTimeAgo } from '@vueuse/core';

import { teamMemberName } from '@tallo/investor/company';
import { useTimeAgoShortMessages } from '@tallo/utility';

import { PropertyListItemDto } from '../dtos/property-list-item.dto.interface';

type PropertyFigures = Pick<PropertyListItemDto, 'rent' | 'bedrooms' | 'fullBathrooms' | 'squareFeet'>;

/** "$1000/mo" while a rent is set */
export function propertyRent(property: PropertyFigures): string {
  return property.rent ? `$${property.rent}/mo` : '';
}

/** The card's second line: the rent, then the figures that are known - "$1000/mo  ∙  1BR, 1BA, 130 sqft" */
export function propertySummary(property: PropertyFigures): string {
  const specifications = [
    property.bedrooms ? `${property.bedrooms}BR` : null,
    property.fullBathrooms ? `${property.fullBathrooms}BA` : null,
    property.squareFeet ? `${property.squareFeet} sqft` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return [propertyRent(property), specifications].filter(Boolean).join('  ∙  ');
}

/** "Listed 3mo ago" once it has been on the market, else "Created 8mo ago" */
export function propertyDateLabel(property: Pick<PropertyListItemDto, 'lastListedAt' | 'createdAt'>): string {
  const prefix = property.lastListedAt ? 'Listed' : 'Created';
  const date = property.lastListedAt ?? property.createdAt;

  return `${prefix} ${useTimeAgo(date, { messages: useTimeAgoShortMessages }).value}`;
}

/** Who runs the unit, marked when it is the viewer: "Yolanda Tillman (you)"; empty without a manager */
export function propertyManagerLabel(
  property: Pick<PropertyListItemDto, 'manager'>,
  viewerId: string | undefined,
): string {
  if (!property.manager) {
    return '';
  }

  const name = teamMemberName(property.manager);

  return property.manager.investorId === viewerId ? `${name} (you)` : name;
}
